import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep, orderBy } from 'lodash';
import { DateTime } from 'luxon';

import { isCampaignDraft, isCampaignRunning } from 'src/data/campaignStatus';
import {
  alignChannelForUnauthorize,
  alignChannelFromBackend,
  alignChannelWithBackend,
  CHANNEL,
  isIntegrationChannel,
  isMetaChannel,
  shouldChannelConnectManually,
} from 'src/data/channel';
import { SORT_DIRECTION } from 'src/data/common';
import {
  convertAccountsListToChannelMap,
  INTEGRATION_ASSET_ACTION,
  mapBackendPerformanceTextVariation,
  PERFORMANCE_EXTRACTION_STATUS,
  prepareAccount,
  VISIBLE_RESOURCES_STATUSES,
} from 'src/data/performance';
import { SOCIAL_AUTH_STATE } from 'src/data/user';
import { pauseCOCampaign, startCOCampaign } from 'src/graphql/continuousOptimization';
import {
  createResourceAccount,
  getPerformanceCenterResultDocument,
  integrationAssetsPerformAction,
  integrationAssetsSearch,
  integrationAuthorize,
  queryChannelsAuthState,
  queryIntegrationAssets,
  queryIntegrationAuthStatus,
  queryIntegrationPerformanceData,
  queryProjectForNewCopies,
  unauthorizeChannel,
  updateAccountName,
} from 'src/graphql/performance';
import { queryWTMProjectsForCIP } from 'src/graphql/project';
import { ExtractionError, isAbortError, isAsyncRequestError } from 'src/lib/errors';
import { captureMessage } from 'src/lib/sentry';
import { connectFacebook } from 'src/services/facebook';

import { isCopiesExtractionError, waitForCopiesExtraction, waitForExtraction } from './utils';

const NAME = 'performance-center-store';

const INITIAL_STATE = {
  loadingAuthState: false,
  connectingToChannelMap: {},

  authStateMap: {},
  authStateFetched: false,

  accountsMap: {},
  customerAccountsMap: {},
  userAccountsMap: {},

  accountsSortField: null,
  accountsSortDirection: null,

  wtmProjects: [],

  copiesMap: {},
  copyPerformanceMap: {},
  lastExtractionDateMap: {},
  campaignsMap: {},
  completionPercentMap: {},
  estimatedActionsDurationMap: {},
  durationFromMap: {},
  extractionStatusMap: {},
  loadingStatusMap: {},
  fetchedDataMap: {},

  campaignsCopyImpressionsMap: {},

  isCreatingResource: false,
  hasFetchedWorkspaceAccounts: false,
  isLoadingWorkspaceAccounts: false,
  isLoadingUserAccounts: false,
  loadingCustomerAccounts: false,

  authErrorMessageMap: {},
  errorMessageMap: {},

  externalConnectionSearchResults: [],
  externalConnectionSearchLoading: false,
};

const channelAbortControllers = new Map();

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  setCopies:
    (channel, accountId, newCopies) =>
    ({ getState, setState }) => {
      if (!newCopies?.length) {
        return;
      }

      const preparedCopies = newCopies.map(mapBackendPerformanceTextVariation);
      const sorted = orderBy(preparedCopies, 'sortIndex', SORT_DIRECTION.asc);

      const { copiesMap } = getState();
      const channelCopies = copiesMap[channel] || {};
      channelCopies[accountId] = sorted;

      setState({ copiesMap: { ...copiesMap, [channel]: channelCopies } });
    },

  setCopyPerformance:
    (channel, accountId, newData) =>
    ({ getState, setState }) => {
      if (!newData?.length) {
        return;
      }

      const newValue = produce(getState().copyPerformanceMap, (draft) => {
        draft[channel] = draft[channel] || {};
        draft[channel][accountId] = newData;
      });

      setState({ copyPerformanceMap: newValue });
    },

  setCompletionPercent:
    (channel, accountId, newCompletionPercent) =>
    ({ getState, setState }) => {
      const { completionPercentMap } = getState();
      const channelCompletionPercent = completionPercentMap[channel] || {};
      channelCompletionPercent[accountId] = newCompletionPercent;

      setState({
        completionPercentMap: {
          ...completionPercentMap,
          [channel]: channelCompletionPercent,
        },
      });
    },

  setEstimatedActionsDuration:
    (channel, accountId, newTotalDuration) =>
    ({ getState, setState, dispatch }) => {
      const { estimatedActionsDurationMap } = getState();

      const channelEstimatedActionsDuration = estimatedActionsDurationMap[channel] || {};

      if (channelEstimatedActionsDuration[accountId]?.length) {
        return;
      }

      dispatch(PRIVATE_ACTIONS.setDurationFrom(channel, accountId, DateTime.local()));

      channelEstimatedActionsDuration[accountId] = newTotalDuration;

      setState({
        estimatedActionsDurationMap: {
          ...estimatedActionsDurationMap,
          [channel]: channelEstimatedActionsDuration,
        },
      });
    },

  resetEstimatedActionsDuration:
    (channel, accountId) =>
    ({ getState, setState }) => {
      const { estimatedActionsDurationMap } = getState();

      const channelEstimatedActionsDuration = estimatedActionsDurationMap[channel] || {};

      channelEstimatedActionsDuration[accountId] = [];

      setState({
        estimatedActionsDurationMap: {
          ...estimatedActionsDurationMap,
          [channel]: channelEstimatedActionsDuration,
        },
      });
    },

  setDurationFrom:
    (channel, accountId, newDurationFrom) =>
    ({ getState, setState }) => {
      const { durationFromMap } = getState();

      const channelDurationFrom = durationFromMap[channel] || {};

      channelDurationFrom[accountId] = newDurationFrom;

      setState({
        durationFromMap: {
          ...durationFromMap,
          [channel]: channelDurationFrom,
        },
      });
    },

  setLastExtractionDate:
    (channel, accountId, connectedAt) =>
    ({ getState, setState }) => {
      const { lastExtractionDateMap, accountsMap } = getState();

      const channelAccounts = accountsMap[channel] || [];

      const account = channelAccounts.find((account) => account.id === accountId);

      if (account) {
        account.connectedAt = connectedAt;
      }

      const channelLastExtractionDate = lastExtractionDateMap[channel] || {};

      channelLastExtractionDate[accountId] = connectedAt;

      setState({
        accountsMap: { ...accountsMap, [channel]: [...channelAccounts] },
        lastExtractionDateMap: { ...lastExtractionDateMap, [channel]: channelLastExtractionDate },
      });
    },

  getLastExtractionDate:
    (channel, accountId) =>
    ({ getState }) => {
      const { lastExtractionDateMap } = getState();

      return lastExtractionDateMap[channel]?.[accountId];
    },

  setCampaigns:
    (channel, accountId, newCampaigns) =>
    ({ getState, setState }) => {
      const { campaignsMap } = getState();

      const channelCampaigns = campaignsMap[channel] || {};

      channelCampaigns[accountId] = newCampaigns;

      setState({
        campaignsMap: { ...campaignsMap, [channel]: channelCampaigns },
      });
    },

  setCampaignsCopyImpressions:
    (channel, accountId, newCampaignsCopyImpressions) =>
    ({ getState, setState }) => {
      const { campaignsCopyImpressionsMap } = getState();

      const channelCampaignsCopyImpressions = campaignsCopyImpressionsMap[channel] || {};

      channelCampaignsCopyImpressions[accountId] = newCampaignsCopyImpressions;

      setState({
        campaignsCopyImpressionsMap: {
          ...campaignsCopyImpressionsMap,
          [channel]: channelCampaignsCopyImpressions,
        },
      });
    },

  getChannelAccounts:
    (channel) =>
    ({ getState }) => {
      const { accountsMap } = getState();

      return accountsMap[channel];
    },

  setChannelAccounts:
    (channel, newAccounts) =>
    ({ getState, setState }) => {
      const { accountsMap } = getState();

      setState({
        accountsMap: { ...accountsMap, [channel]: newAccounts },
      });
    },

  setLoadingStatus:
    (channel, accountId, loading) =>
    ({ getState, setState }) => {
      const { loadingStatusMap } = getState();

      setState({
        loadingStatusMap: {
          ...loadingStatusMap,
          [channel]: { ...loadingStatusMap[channel], [accountId]: loading },
        },
      });
    },

  setFetchedData:
    (channel, accountId, fetched) =>
    ({ getState, setState }) => {
      const { fetchedDataMap } = getState();

      setState({
        fetchedDataMap: {
          ...fetchedDataMap,
          [channel]: { ...fetchedDataMap[channel], [accountId]: fetched },
        },
      });
    },

  setConnectingToChannel:
    (channel, connecting) =>
    ({ getState, setState }) => {
      const { connectingToChannelMap } = getState();

      setState({
        connectingToChannelMap: { ...connectingToChannelMap, [channel]: connecting },
      });
    },

  getIsLoading:
    (channel, accountId) =>
    ({ getState }) => {
      const { loadingStatusMap } = getState();
      return loadingStatusMap[channel]?.[accountId];
    },

  getExtractionStatus:
    (channel, accountId) =>
    ({ getState }) => {
      const { extractionStatusMap } = getState();
      return extractionStatusMap[channel]?.[accountId];
    },

  mergeToAccountList:
    (channel, account) =>
    ({ getState, setState }) => {
      const { accountsMap, customerAccountsMap } = getState();

      const channelAccounts = accountsMap[channel] || [];

      if (channelAccounts.some(({ id }) => account.id === id)) {
        return;
      }

      setState({
        accountsMap: { ...accountsMap, [channel]: [...(accountsMap[channel] || []), account] },
        customerAccountsMap: {
          ...(customerAccountsMap || {}),
          [channel]: [...(customerAccountsMap[channel] || []), account],
        },
      });
    },

  addToAccountList:
    (channel, account) =>
    ({ getState, setState }) => {
      const { accountsMap, customerAccountsMap } = getState();

      setState({
        accountsMap: { ...accountsMap, [channel]: [...(accountsMap[channel] || []), account] },
        customerAccountsMap: {
          ...(customerAccountsMap || {}),
          [channel]: [...(customerAccountsMap[channel] || []), account],
        },
      });
    },

  setChannelAuthState:
    (channel, authState) =>
    ({ getState, setState }) => {
      const { authStateMap } = getState();

      let updatedAuthState;
      if (isMetaChannel(channel)) {
        updatedAuthState = {
          ...authStateMap,
          [CHANNEL.facebook]: authState,
          [CHANNEL.facebookPages]: authState,
          [CHANNEL.instagram]: authState,
        };
      } else {
        updatedAuthState = { ...authStateMap, [channel]: authState };
      }

      setState({
        authStateMap: updatedAuthState,
      });
    },

  removeAccountFromChannel:
    (channel, accountId) =>
    ({ getState, setState }) => {
      const {
        accountsMap,
        lastExtractionDateMap,
        copiesMap,
        copyPerformanceMap,
        loadingStatusMap,
        customerAccountsMap,
        userAccountsMap,
      } = getState();

      const channelAccounts = accountsMap[channel].map((account) =>
        account.id === accountId
          ? { ...account, status: PERFORMANCE_EXTRACTION_STATUS.disconnected }
          : account
      );

      const channelCustomerAccountsMap = (customerAccountsMap[channel] || []).map((account) =>
        account.id === accountId
          ? { ...account, status: PERFORMANCE_EXTRACTION_STATUS.disconnected }
          : account
      );

      const channelUserAccountsMap = (userAccountsMap[channel] || []).map((account) =>
        account.id === accountId
          ? { ...account, status: PERFORMANCE_EXTRACTION_STATUS.disconnected }
          : account
      );

      const channelCopies = copiesMap[channel] || {};
      delete channelCopies[accountId];

      const newCopyPerformanceMap = produce(copyPerformanceMap, (draft) => {
        if (draft[channel]?.[accountId]) {
          delete draft[channel][accountId];
        }
      });

      const channelLastExtractionDate = lastExtractionDateMap[channel] || {};
      delete channelLastExtractionDate[accountId];

      const channelLoadingStatus = loadingStatusMap[channel] || {};
      delete channelLoadingStatus[accountId];

      setState({
        accountsMap: { ...accountsMap, [channel]: channelAccounts },
        lastExtractionDateMap: { ...lastExtractionDateMap, [channel]: channelLastExtractionDate },
        copiesMap: { ...copiesMap, [channel]: channelCopies },
        copyPerformanceMap: newCopyPerformanceMap,
        loadingStatusMap: { ...loadingStatusMap, [channel]: channelLoadingStatus },
        customerAccountsMap: {
          ...(customerAccountsMap || []),
          [channel]: channelCustomerAccountsMap,
        },
        userAccountsMap: { ...(userAccountsMap || []), [channel]: channelUserAccountsMap },
      });
    },

  extractWithPolling:
    ({ channel, accountId, onConnectionError }) =>
    async ({ dispatch }, { currentCustomerId }) => {
      channelAbortControllers.set(channel, new AbortController());

      dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, true));
      dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 1));
      try {
        const results = await waitForCopiesExtraction({
          dataSource: alignChannelWithBackend(channel),
          customerId: currentCustomerId,
          accountId,
          onStatus: ({ status, completionPercent, estimatedDuration }) => {
            if (status) {
              dispatch(ACTIONS.mergeAccountStatus(channel, accountId, status));
            }
            if (completionPercent) {
              dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, completionPercent));
            }
            if (estimatedDuration?.length > 0) {
              dispatch(
                PRIVATE_ACTIONS.setEstimatedActionsDuration(channel, accountId, estimatedDuration)
              );
            }
          },
          onConnectionError,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
        });
        channelAbortControllers.delete(channel);

        if (!results?.length) {
          throw new Error("Couldn't extract data");
        }

        const result = results[0];

        dispatch(PRIVATE_ACTIONS.setLastExtractionDate(channel, accountId, result?.connectedAt));
        if (isIntegrationChannel(channel)) {
          dispatch(
            PRIVATE_ACTIONS.setCopies(channel, accountId, result?.integrationAssetResults?.nodes)
          );
          // dispatch(PRIVATE_ACTIONS.setCopyPerformance(channel, accountId, result?.performanceData)); // TODO: Uncomment when we will be fetching "performance data" along with other integration data
          dispatch(PRIVATE_ACTIONS.setCampaigns(channel, accountId, result?.campaigns));
          dispatch(
            PRIVATE_ACTIONS.setCampaignsCopyImpressions(
              channel,
              accountId,
              result?.campaignsCopyImpressions
            )
          );
        }
        return result;
      } catch (error) {
        dispatch(PRIVATE_ACTIONS.setLastExtractionDate(channel, accountId, null));
        dispatch(PRIVATE_ACTIONS.setErrorMessage(channel, accountId, error.message));

        // Ensure extraction status is set to error when extraction fails
        dispatch(
          ACTIONS.mergeAccountStatus(channel, accountId, PERFORMANCE_EXTRACTION_STATUS.error)
        );

        if (isAsyncRequestError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState());
          // Don't re-throw AsyncRequestError - handle it completely here
          return;
        }
        if (isCopiesExtractionError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState(true));
          dispatch(ACTIONS.setAuthErrorMessage(channel, error.message));
          return;
        }
        if (!isAbortError(error)) {
          throw error;
        }
        dispatch(
          ACTIONS.mergeAccountStatus(channel, accountId, PERFORMANCE_EXTRACTION_STATUS.disconnected)
        );
      } finally {
        dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, false));
        dispatch(PRIVATE_ACTIONS.setFetchedData(channel, accountId, true));
      }
    },

  setErrorMessage:
    (channel, accountId, message) =>
    async ({ getState, setState }) => {
      const { errorMessageMap } = getState();

      const channelErrorMessage = errorMessageMap[channel] || {};

      channelErrorMessage[accountId] = message;

      setState({
        errorMessageMap: { ...errorMessageMap, [channel]: channelErrorMessage },
      });
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.clearStore());
    },

  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  queryCustomerAccounts:
    () =>
    async ({ setState }, { notifications, currentCustomerId }) => {
      try {
        setState({ loadingCustomerAccounts: true });
        const integrationAssets = await queryIntegrationAssets({
          customerId: currentCustomerId,
          statuses: VISIBLE_RESOURCES_STATUSES,
        });

        if (!integrationAssets) {
          captureMessage('"queryIntegrationAssets" GQL query returned empty data', {
            level: 'warning',
            extra: {
              source: 'PerformanceStore -> queryCustomerAccounts',
            },
          });
          throw new Error('Customer invalid');
        }

        setState({
          customerAccountsMap: convertAccountsListToChannelMap(integrationAssets),
        });
      } catch (error) {
        notifications.displayError(error);
        captureMessage('Error while calling "queryIntegrationAssets" GQL query', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'PerformanceStore -> queryCustomerAccounts',
          },
        });
      } finally {
        setState({ loadingCustomerAccounts: false });
      }
    },

  queryWorkspaceAccounts:
    (workspaceId) =>
    async ({ setState }, { notifications, currentWorkspaceId, currentCustomerId }) => {
      try {
        setState({ isLoadingWorkspaceAccounts: true });
        const integrationAssets = await queryIntegrationAssets({
          customerId: currentCustomerId,
          workspaceId: workspaceId || currentWorkspaceId,
          statuses: VISIBLE_RESOURCES_STATUSES,
        });

        if (!integrationAssets) {
          captureMessage('"queryIntegrationAssets" GQL query returned empty data', {
            level: 'warning',
            extra: {
              source: 'PerformanceStore -> queryWorkspaceAccounts',
            },
          });
          throw new Error('Customer invalid');
        }

        setState({
          accountsMap: convertAccountsListToChannelMap(integrationAssets),
        });
      } catch (error) {
        notifications.displayError(error);
        captureMessage('Error while calling "queryIntegrationAssets" GQL query', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'PerformanceStore -> queryWorkspaceAccounts',
          },
        });
      } finally {
        setState({
          hasFetchedWorkspaceAccounts: true,
          isLoadingWorkspaceAccounts: false,
        });
      }
    },

  queryUserIntegrationAccounts:
    (userId) =>
    async ({ setState }, { notifications, currentUserId, currentCustomerId }) => {
      try {
        setState({ isLoadingUserAccounts: true });

        const integrationAssets = await queryIntegrationAssets({
          userId: userId || currentUserId,
          customerId: currentCustomerId,
        });

        if (!integrationAssets) {
          captureMessage('"queryIntegrationAssets" GQL query returned empty data', {
            level: 'warning',
            extra: {
              source: 'PerformanceStore -> queryCurrentUserIntegrationAccounts',
            },
          });
          throw new Error('User invalid');
        }

        const userAccountsMap = convertAccountsListToChannelMap(integrationAssets);

        setState({
          userAccountsMap,
        });
        return userAccountsMap;
      } catch (error) {
        notifications.displayError(error);
        captureMessage('Error while calling "queryIntegrationAssets" GQL query', {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'PerformanceStore -> queryUserIntegrationAccounts',
          },
        });
      } finally {
        setState({ isLoadingUserAccounts: false });
      }
    },

  refreshIntegrationAssets:
    (channel) =>
    async ({ getState, setState, dispatch }, { currentUserId, currentCustomerId }) => {
      const dataSource = alignChannelWithBackend(channel);
      try {
        setState({ isLoadingUserAccounts: true });
        const integrationAssets = await queryIntegrationAssets({
          userId: currentUserId,
          customerId: currentCustomerId,
          dataSource,
          refresh: true,
        });
        const refreshedChannelMap = convertAccountsListToChannelMap(integrationAssets);
        const { userAccountsMap } = getState();
        setState({
          userAccountsMap: {
            ...userAccountsMap,
            ...refreshedChannelMap,
          },
        });
      } finally {
        setState({ isLoadingUserAccounts: false });
      }
    },

  queryChannelsAuthState:
    (force) =>
    async ({ getState, setState }, { notifications }) => {
      const { authStateFetched } = getState();
      if (authStateFetched && !force) {
        return;
      }

      setState({ loadingAuthState: true });
      try {
        const {
          googleAuthState,
          facebookAuthState,
          linkedinAuthState,
          linkedinSocialAuthState,
          hubspotAuthState,
          salesforceData,
          twitterSocialData,
        } = await queryChannelsAuthState();

        const { integrationAuthStatus } = await queryIntegrationAuthStatus();

        const { authStateMap } = getState();
        authStateMap[CHANNEL.google] = googleAuthState;
        authStateMap[CHANNEL.facebook] = facebookAuthState;
        authStateMap[CHANNEL.linkedin] = linkedinAuthState;
        authStateMap[CHANNEL.linkedinSocial] = linkedinSocialAuthState;
        authStateMap[CHANNEL.hubspot] = hubspotAuthState;
        authStateMap[CHANNEL.facebookPages] = facebookAuthState;
        authStateMap[CHANNEL.instagram] = facebookAuthState;
        authStateMap[CHANNEL.salesforce] = salesforceData;
        authStateMap[CHANNEL.twitterSocial] = twitterSocialData;

        integrationAuthStatus.forEach((integrationAuthStatus) => {
          authStateMap[alignChannelFromBackend(integrationAuthStatus.dataSource)] =
            integrationAuthStatus.authState;
        });

        setState({
          authStateMap,
          authStateFetched: true,
        });
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setState({ loadingAuthState: false });
      }
    },

  connectToMetaChannel:
    (channel, options) =>
    async ({ dispatch }, { currentCustomerId }) => {
      dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, true));

      try {
        const result = await connectFacebook(currentCustomerId);
        if (!options?.skipAccountsLoading) {
          await dispatch(ACTIONS.queryUserIntegrationAccounts());
        }
        dispatch(PRIVATE_ACTIONS.setChannelAuthState(channel, result?.authState));
        return result?.authState === SOCIAL_AUTH_STATE.authorized;
      } finally {
        dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, false));
      }
    },

  isChannelConnected:
    (channel) =>
    ({ getState }) => {
      const { authStateMap } = getState();
      if (shouldChannelConnectManually(channel)) {
        return true;
      }
      return authStateMap[channel] === SOCIAL_AUTH_STATE.authorized;
    },

  disconnectFromChannel:
    (channel) =>
    async ({ dispatch }, { currentCustomerId }) => {
      dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, true));

      const dataSource = alignChannelForUnauthorize(channel);
      try {
        await unauthorizeChannel({ channel: dataSource, customerId: currentCustomerId });

        dispatch(PRIVATE_ACTIONS.setChannelAuthState(channel, SOCIAL_AUTH_STATE.unauthorized));
        const channelAccounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));

        channelAccounts?.forEach((account) => {
          dispatch(PRIVATE_ACTIONS.removeAccountFromChannel(channel, account.id));
        });
      } finally {
        dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, false));
      }
    },

  externalConnectionConnect:
    ({ accountId, channel, workspaceId, integrationAssetId }) =>
    async ({ dispatch }, { currentCustomerId, notifications }) => {
      try {
        const account = await integrationAssetsPerformAction({
          customerId: currentCustomerId,
          accountId,
          dataSource: alignChannelWithBackend(channel),
          workspaceId,
          integrationAssetId,
          actionsList: [INTEGRATION_ASSET_ACTION.connect],
        });

        const preparedAccount = prepareAccount(account, channel);

        dispatch(PRIVATE_ACTIONS.addToAccountList(channel, preparedAccount));
        dispatch(
          ACTIONS.mergeAccountStatus(
            channel,
            preparedAccount.accountId,
            PERFORMANCE_EXTRACTION_STATUS.connected
          )
        );
      } catch (error) {
        notifications.displayError(error);
      }
    },

  createResourceAccount:
    ({ csvFile, channel, brandVoiceId, onCreateSuccess, onConnectSuccess, createTalkingPoints }) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ isCreatingResource: true });

      try {
        const account = await createResourceAccount({
          customerId: currentCustomerId,
          name: csvFile?.name.replace('.csv', ' (.csv)'),
          file: csvFile,
          dataSource: channel,
        });

        const preparedAccount = {
          name: account.accountName,
          accountId: account.id,
          channel,
          ...account,
        };
        onCreateSuccess?.(preparedAccount);
        await dispatch(
          ACTIONS.connectPerformanceCenterAccount({
            account: preparedAccount,
            workspaceId: currentWorkspaceId,
            customModelBrandVoiceId: brandVoiceId,
            onConnectSuccess,
            createTalkingPoints,
          })
        );
        return preparedAccount;
      } finally {
        setState({ isCreatingResource: false });
      }
    },

  resetCompletionPercent:
    (channel, accountId) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 0));
    },

  displayExtractingProgressIndicator:
    (channel, accountId, promise) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 0));
      dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, true));

      try {
        await promise;
      } finally {
        dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 100));
        dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, false));
      }
    },

  connectPerformanceCenterAccount:
    ({ account, workspaceId, customModelBrandVoiceId, onConnectSuccess, createTalkingPoints }) =>
    async ({ dispatch }, { notifications, currentCustomerId }) => {
      const { channel, integrationAssetId, accountId } = account;

      dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 0));
      dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, true));

      const alignedChannel = alignChannelWithBackend(channel);

      channelAbortControllers.set(channel, new AbortController());

      dispatch(PRIVATE_ACTIONS.mergeToAccountList(channel, account));

      const actionsList = [
        INTEGRATION_ASSET_ACTION.connect,
        INTEGRATION_ASSET_ACTION.dataDownload,
        INTEGRATION_ASSET_ACTION.createBenchmarkModel,
      ];

      if (customModelBrandVoiceId) {
        actionsList.push(INTEGRATION_ASSET_ACTION.createCustomModel);
      }

      try {
        await integrationAssetsPerformAction(
          {
            customerId: currentCustomerId,
            workspaceId,
            integrationAssetId,
            accountId,
            dataSource: alignedChannel,
            actionsList,
            customModelBrandVoiceId,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );
        const result = await dispatch(
          PRIVATE_ACTIONS.extractWithPolling({
            channel,
            accountId,
          })
        );

        dispatch(PRIVATE_ACTIONS.setDurationFrom(channel, accountId, null));
        dispatch(PRIVATE_ACTIONS.resetEstimatedActionsDuration(channel, accountId, []));

        onConnectSuccess?.({
          customModelId: result.customDatasetId,
          brandVoiceId: customModelBrandVoiceId,
          account,
        });

        return result;
      } catch (error) {
        if (!isAbortError(error)) {
          notifications.displayError(error);
        }
      }
    },

  refreshAccount:
    ({ channel, accountId }) =>
    async ({ dispatch }, { notifications, currentCustomerId }) => {
      dispatch(PRIVATE_ACTIONS.setCompletionPercent(channel, accountId, 0));
      dispatch(PRIVATE_ACTIONS.setLoadingStatus(channel, accountId, true));

      const channelAccounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));

      const account = channelAccounts.find(
        (channelAccount) => channelAccount.accountId === accountId
      );

      channelAbortControllers.set(channel, new AbortController());
      try {
        await integrationAssetsPerformAction(
          {
            customerId: currentCustomerId,
            accountId,
            integrationAssetId: account.integrationAssetId,
            dataSource: alignChannelWithBackend(channel),
            actionsList: [INTEGRATION_ASSET_ACTION.dataDownload],
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );

        return dispatch(
          PRIVATE_ACTIONS.extractWithPolling({
            channel,
            accountId,
            onConnectionError: notifications.displayError,
          })
        );
      } catch (error) {
        if (!isAbortError(error)) {
          notifications.displayError(error);
        }
      }
    },

  disconnectAccount:
    (account) =>
    async ({ dispatch }, { currentCustomerId, currentWorkspaceId, notifications }) => {
      try {
        await integrationAssetsPerformAction({
          customerId: currentCustomerId,
          workspaceId: currentWorkspaceId,
          integrationAssetId: account.integrationAssetId,
          accountId: account.id,
          dataSource: account.dataSource,
          actionsList: [INTEGRATION_ASSET_ACTION.disconnect],
        });

        dispatch(PRIVATE_ACTIONS.removeAccountFromChannel(account.channel, account.id));
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getLastExtractionDate: PRIVATE_ACTIONS.getLastExtractionDate,
  setLastExtractionDate: PRIVATE_ACTIONS.setLastExtractionDate,
  getLastExtraction:
    (channel, accountId) =>
    async ({ dispatch }) => {
      const channelLastExtractionDate = dispatch(
        PRIVATE_ACTIONS.getLastExtractionDate(channel, accountId)
      );
      const loadingStatus = dispatch(PRIVATE_ACTIONS.getIsLoading(channel, accountId));
      const extractionStatus = dispatch(PRIVATE_ACTIONS.getExtractionStatus(channel, accountId));

      // don't fetch copies again if already did on same session
      if (
        channelLastExtractionDate ||
        loadingStatus ||
        extractionStatus === PERFORMANCE_EXTRACTION_STATUS.nullableResults ||
        extractionStatus === PERFORMANCE_EXTRACTION_STATUS.error
      ) {
        return;
      }

      return dispatch(PRIVATE_ACTIONS.extractWithPolling({ channel, accountId }));
    },

  queryPerformanceData:
    ({ channel, accountId }) =>
    async ({ setState, dispatch }, { currentCustomerId }) => {
      const results = await queryIntegrationPerformanceData({
        customerId: currentCustomerId,
        dataSource: alignChannelWithBackend(channel),
        accountId,
      });
      if (!results?.length) {
        throw new ExtractionError('Could not extract data');
      }

      const result = results[0];
      dispatch(PRIVATE_ACTIONS.setCopyPerformance(channel, accountId, result?.performanceData));
    },
  getPerformanceData:
    ({ channel, accountId }) =>
    ({ getState }) => {
      const { copyPerformanceMap } = getState();
      return copyPerformanceMap[channel]?.[accountId];
    },

  sortAccounts:
    (field, channel) =>
    ({ getState, setState, dispatch }) => {
      const channelAccounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));
      const { accountsSortField, accountsSortDirection } = getState();

      const sameField = accountsSortField === field;
      const sortedAsc = accountsSortDirection === SORT_DIRECTION.asc;
      const newDirection = sameField && sortedAsc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc;

      const sorted = orderBy(channelAccounts, [field], [newDirection]);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, sorted));

      setState({
        accountsSortField: field,
        accountsSortDirection: newDirection,
      });
    },

  getPerformanceCenterResultDocument:
    (adId) =>
    async (_, { notifications, currentCustomerId }) => {
      try {
        return await getPerformanceCenterResultDocument({
          performanceCenterTextVariationsResultId: adId,
          customerId: currentCustomerId,
        });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getProjectForNewCopies:
    () =>
    async (_, { currentWorkspaceId }) => {
      return queryProjectForNewCopies({ workspaceId: currentWorkspaceId });
    },

  getCopies:
    ({ channel, accountId }) =>
    ({ getState }) => {
      const { copiesMap } = getState();
      return copiesMap[channel]?.[accountId] || [];
    },

  cancelWaitRequests: (channel) => () => {
    if (channel) {
      if (channelAbortControllers.has(channel)) {
        channelAbortControllers.get(channel)?.abort();
        channelAbortControllers.delete(channel);
      }
    } else {
      channelAbortControllers.forEach((channelAbortController) => {
        channelAbortController.abort();
      });
      channelAbortControllers.clear();
    }
  },

  setAuthErrorMessage:
    (channel, message) =>
    ({ getState, setState }) => {
      const { authErrorMessageMap } = getState();

      setState({
        authErrorMessageMap: {
          ...authErrorMessageMap,
          [channel]: message,
        },
      });
    },
  clearAuthErrorMessage:
    (channel) =>
    ({ getState, setState }) => {
      const { authErrorMessageMap } = getState();

      delete authErrorMessageMap[channel];

      setState({
        authErrorMessageMap,
      });
    },

  /**
   * Refresh WTM integration info: "auth state" and "active projects list"
   * @param {string} workspaceId - workspace id
   * @return {Promise}
   */
  refreshWTMIntegrationInfo:
    (workspaceId) =>
    async ({ setState, dispatch }, { currentCustomerId, currentWorkspaceId }) => {
      try {
        const result = await queryWTMProjectsForCIP({
          customerId: currentCustomerId,
          workspaceId: workspaceId || currentWorkspaceId,
        });
        const withNonDraftCampaign = result.filter(
          (project) => !isCampaignDraft(project?.continuousOptimizationCampaign?.status)
        );

        // Store WTM projects to display on "manage integration" popup
        setState({ wtmProjects: withNonDraftCampaign });

        // Store "WTM project" auth state
        const authState =
          withNonDraftCampaign.length > 0
            ? SOCIAL_AUTH_STATE.authorized
            : SOCIAL_AUTH_STATE.unauthorized;
        dispatch(PRIVATE_ACTIONS.setChannelAuthState(CHANNEL.projectWtm, authState));
      } catch {
        // In case of an error - set auth state to "unauthorized"
        dispatch(
          PRIVATE_ACTIONS.setChannelAuthState(CHANNEL.projectWtm, SOCIAL_AUTH_STATE.unauthorized)
        );
      }
    },

  /**
   * Start/Pause whole WTM campaign and get the new "status"
   * @param {string} projectId - project id
   * @return {Promise<CAMPAIGN_STATUS>} - new campaign status
   */
  toggleWTMIntegrationStatus:
    (projectId) =>
    async ({ getState, setState }) => {
      // Get project and campaign from stored list
      const { wtmProjects } = getState();
      const project = wtmProjects.find((item) => item.id === projectId);
      const campaign = project?.continuousOptimizationCampaign;
      if (!campaign) {
        throw new Error('WTM campaign not found');
      }

      // Start or pause campaign and get the new status
      const result = isCampaignRunning(campaign.status)
        ? await pauseCOCampaign({ id: campaign.id })
        : await startCOCampaign({ id: campaign.id });

      // Rewrite status in stored projects
      const newList = wtmProjects.map((item) => {
        if (item.id === projectId) {
          return {
            ...item,
            continuousOptimizationCampaign: {
              ...item.continuousOptimizationCampaign,
              status: result.status,
            },
          };
        }
        return item;
      });
      setState({ wtmProjects: newList });

      // Return new status
      return result.status;
    },

  removeWTMIntegrationProject:
    (projectId) =>
    ({ getState, setState }) => {
      const { wtmProjects } = getState();
      const newList = wtmProjects.filter((item) => item.id !== projectId);
      setState({ wtmProjects: newList });
    },

  mergeAccountStatus:
    (channel, accountId, status) =>
    ({ getState, setState }) => {
      const { accountsMap, extractionStatusMap } = getState();

      const channelAccounts = accountsMap[channel] || [];

      const updatedAccounts = channelAccounts.map((account) => {
        if (account.id === accountId) {
          return { ...account, status };
        }
        return account;
      });

      setState({
        accountsMap: { ...accountsMap, [channel]: updatedAccounts },
        extractionStatusMap: {
          ...extractionStatusMap,
          [channel]: { ...extractionStatusMap[channel], [accountId]: status },
        },
      });
    },

  mergeAccountName:
    (channel, accountId, name) =>
    ({ getState, setState }) => {
      if (!name) {
        return;
      }
      const { accountsMap, customerAccountsMap } = getState();

      const channelAccounts = accountsMap[channel] || [];

      const customerChannelAccounts = customerAccountsMap[channel] || [];

      const updatedChannelAccounts = channelAccounts.map((account) => {
        if (account.id === accountId) {
          return { ...account, name };
        }
        return account;
      });

      const updatedCustomerChannelAccounts = customerChannelAccounts.map((account) => {
        if (account.id === accountId) {
          return { ...account, name };
        }
        return account;
      });

      setState({
        accountsMap: { ...accountsMap, [channel]: updatedChannelAccounts },
        channelAccounts: { ...channelAccounts, [channel]: updatedCustomerChannelAccounts },
      });
    },

  mergeToAccountList: PRIVATE_ACTIONS.mergeToAccountList,

  renameChannelAccount:
    (account, newAccountName) =>
    async ({ dispatch }, { currentCustomerId }) => {
      const result = await updateAccountName({
        customerId: currentCustomerId,
        accountId: account.id,
        dataSource: alignChannelWithBackend(account.channel),
        newAccountName,
      });
      dispatch(ACTIONS.mergeAccountName(account.channel, account.id, result?.name));
    },

  integrationAuthorize:
    (channel, credentials, options) =>
    async ({ dispatch }, { currentCustomerId }) => {
      await integrationAuthorize({
        customerId: currentCustomerId,
        dataSource: alignChannelWithBackend(channel),
        credentials,
      });

      let userAccountsMap = {};

      if (!options?.skipAccountsLoading) {
        userAccountsMap = await dispatch(ACTIONS.queryUserIntegrationAccounts());
      }

      await dispatch(PRIVATE_ACTIONS.setChannelAuthState(channel, SOCIAL_AUTH_STATE.authorized));
      return userAccountsMap[channel]?.[0];
    },

  searchIntegrationAssets:
    ({ channel, searchTerm, filter, workspaceId }) =>
    async ({ setState }, { currentCustomerId }) => {
      try {
        const job = await integrationAssetsSearch({
          customerId: currentCustomerId,
          dataSource: alignChannelWithBackend(channel),
          searchTerm,
          filter,
          workspaceId,
        });

        const result = await waitForExtraction({ id: job.id });

        setState({ externalConnectionSearchResults: result });
      } catch (error) {
        if (!isAbortError(error)) {
          throw error;
        }
      } finally {
        setState({ externalConnectionSearchLoading: false });
      }
    },

  setExternalConnectionSearchLoading:
    (externalConnectionSearchLoading) =>
    ({ setState }) => {
      setState({ externalConnectionSearchLoading });
    },
};

export const PerformanceStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
